import { sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { BasicUser } from "types/topicflow";

import {
  currentOrganizationVar,
  currentUserVar,
  successNotificationVar,
} from "@cache/cache";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import {
  assertEdgesNonNull,
  assertNonNull,
  getLocalStorage,
  setLocalStorage,
} from "@helpers/helpers";

import ImpersonateUser from "./impersonate-user";

const FAV_LOGINS_STORAGE_KEY = "topicflow:fav-login-as";

const getFavoritesFromLocalStorage = () =>
  getLocalStorage<BasicUser[]>(FAV_LOGINS_STORAGE_KEY, []);

const useImpersonateList = () => {
  const org = currentOrganizationVar();
  const currentUser = currentUserVar();
  const [isOpen, setIsOpen] = useState(false);
  const [favoritedLogins, setFavoriteLogins] = useState(
    getFavoritesFromLocalStorage(),
  );

  const allOrgMembers: BasicUser[] = useMemo(() => {
    return org.allMembers
      ? assertEdgesNonNull(org.allMembers)
          .map((edge) => ({
            ...assertNonNull(edge.user),
          }))
          .filter(({ id }) => !favoritedLogins.find((user) => user.id === id))
      : [];
  }, [org, favoritedLogins]);

  const handleOpenModal = useCallback(() => {
    if (
      !window.TF.can_hijack ||
      (allOrgMembers.length === 0 && favoritedLogins.length === 0)
    ) {
      return;
    }
    setIsOpen(true);
  }, [allOrgMembers, favoritedLogins]);

  const handleSaveFavoriteLogin = useCallback(
    (user: BasicUser) => {
      const newLogins = [
        ...favoritedLogins.filter(({ id }) => id !== user.id),
        {
          id: user.id,
          name: user.name,
          email: user.email,
        },
      ];
      setLocalStorage<BasicUser[]>(FAV_LOGINS_STORAGE_KEY, newLogins);
      setFavoriteLogins(newLogins);
    },
    [favoritedLogins],
  );

  const handleSaveCurrentLoginAsFavorite = useCallback(() => {
    if (!window.TF.hijacked) {
      return;
    }
    handleSaveFavoriteLogin(currentUser);
    successNotificationVar({ title: "Login marked as favorite" });
  }, [currentUser, handleSaveFavoriteLogin]);

  const handleRemoveFavoriteLogin = useCallback(
    (userId: number) => {
      const newLogins = [...favoritedLogins.filter(({ id }) => id !== userId)];
      setLocalStorage<BasicUser[]>(FAV_LOGINS_STORAGE_KEY, newLogins);
      setFavoriteLogins(newLogins);
    },
    [favoritedLogins],
  );

  useHotkeys("i>i", handleOpenModal);
  useHotkeys("i>f>f", handleSaveCurrentLoginAsFavorite);

  const modalContent = useMemo(() => {
    if (!isOpen) {
      return null;
    }

    return (
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        alignedTop
        className="z-tooltip"
      >
        <div className="p-6 flex flex-col gap-4">
          <ModalTitle onClose={() => setIsOpen(false)}>Impersonate</ModalTitle>
          <div className="flex flex-col gap-3">
            {sortBy(favoritedLogins, "name").map((user) => {
              return (
                <ImpersonateUser
                  key={user.id}
                  user={user}
                  isFavorited
                  onClickFavorite={() => handleRemoveFavoriteLogin(user.id)}
                />
              );
            })}

            {sortBy(
              allOrgMembers.filter(({ id }) => id !== currentUser.id),
              "name",
            ).map((user) => {
              return (
                <ImpersonateUser
                  key={user.id}
                  user={user}
                  onClickFavorite={() => handleSaveFavoriteLogin(user)}
                />
              );
            })}
          </div>
        </div>
      </Modal>
    );
  }, [
    allOrgMembers,
    currentUser,
    favoritedLogins,
    isOpen,
    handleSaveFavoriteLogin,
    handleRemoveFavoriteLogin,
  ]);

  return {
    ImpersonateModal: modalContent,
  };
};

export default useImpersonateList;
